import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { ShieldCheck, ShieldAlert, Copy } from "lucide-react";
import { toast } from "sonner";

export default function Profile() {
  const { user, refresh } = useAuth();
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const startSetup = async () => {
    setErr(""); setBusy(true);
    try {
      const { data } = await api.get("/auth/2fa-setup");
      setSetup(data);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const enable = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await api.post("/auth/enable-2fa", { code });
      toast.success("Two-factor authentication enabled.");
      setSetup(null);
      await refresh();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  if (!user) return null;

  return (
    <div className="max-w-3xl mx-auto px-6 py-10">
      <div className="mb-8">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-2">Profile</div>
        <h1 className="text-4xl font-display font-bold tracking-tight text-slate-900">Account settings</h1>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-6 mb-6">
        <div className="grid sm:grid-cols-3 gap-4 text-sm">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Name</div>
            <div className="mt-1 font-medium text-slate-900" data-testid="profile-name">{user.name}</div>
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Email</div>
            <div className="mt-1 font-medium text-slate-900 break-all" data-testid="profile-email">{user.email}</div>
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Role</div>
            <div className="mt-1"><Badge variant="outline" className="capitalize">{user.role}</Badge></div>
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-6 mb-6">
        <div className="flex items-center gap-3 mb-4">
          {user.two_factor_enabled ? (
            <><ShieldCheck className="w-6 h-6 text-emerald-600" /><span className="font-display font-bold">Two-factor authentication enabled</span></>
          ) : (
            <><ShieldAlert className="w-6 h-6 text-amber-600" /><span className="font-display font-bold">Two-factor authentication not enabled</span></>
          )}
        </div>
        {user.two_factor_enabled ? (
          <p className="text-sm text-slate-600">Your account is protected by Google Authenticator (TOTP). Required at every login.</p>
        ) : !setup ? (
          <Button onClick={startSetup} disabled={busy} data-testid="profile-enable-2fa-button">
            {busy ? "Loading…" : "Enable 2FA"}
          </Button>
        ) : (
          <div className="space-y-4">
            <div className="border border-slate-200 rounded-lg p-4 flex justify-center bg-slate-50">
              <img src={setup.qr_data_url} alt="TOTP QR" className="w-44 h-44" data-testid="profile-totp-qr" />
            </div>
            <div>
              <Label className="text-xs">Secret (manual entry)</Label>
              <div className="flex gap-2 mt-1">
                <Input readOnly value={setup.secret} className="font-mono text-sm" />
                <Button variant="outline" size="icon" onClick={() => navigator.clipboard?.writeText(setup.secret)}>
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
            {err && <Alert variant="destructive"><AlertDescription>{err}</AlertDescription></Alert>}
            <form onSubmit={enable} className="space-y-3">
              <Label htmlFor="otp2">Enter 6-digit code</Label>
              <Input id="otp2" data-testid="profile-totp-code-input" inputMode="numeric" maxLength={6} required value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                className="font-mono text-lg tracking-widest" />
              <Button type="submit" disabled={busy} data-testid="profile-totp-enable-button" className="w-full">
                {busy ? "Enabling…" : "Enable 2FA"}
              </Button>
            </form>
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-6">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-3">Subscription</div>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="font-display font-bold text-slate-900 capitalize" data-testid="profile-subscription-status">
              {user.subscription_status === "active" ? `Active · ${user.subscription_plan}` : user.subscription_status}
            </div>
            {user.subscription_current_period_end && (
              <div className="text-xs text-slate-600 mt-1">
                Renews {new Date(user.subscription_current_period_end).toLocaleDateString()}
              </div>
            )}
          </div>
          <a href="/subscription"><Button variant="outline" size="sm">Manage</Button></a>
        </div>
      </div>
    </div>
  );
}
